import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { cn } from './cn.js';
import { Button } from './Button.js';

export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly children: React.ReactNode;
  readonly confirmLabel: string;
  readonly tone?: 'signal' | 'alarm';
  readonly busy?: boolean;
  readonly initialFocus?: 'confirm' | 'cancel';
  readonly size?: 'md' | 'lg' | 'xl' | '2xl';
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

/**
 * Modern modal dialog for bulk and destructive operations.
 * Always previewed before execution with clear confirmation.
 * Restores focus to the triggering element on close and prevents unwanted page scrolls.
 */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  tone = 'signal',
  busy = false,
  initialFocus = 'confirm',
  size = 'lg',
  onConfirm,
  onCancel,
}: ConfirmDialogProps): React.ReactElement | null {
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  const confirmRef = useRef(onConfirm);
  confirmRef.current = onConfirm;

  const panelRef = useRef<HTMLDivElement>(null);
  const confirmBtnRef = useRef<HTMLButtonElement>(null);
  const cancelBtnRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    previouslyFocusedRef.current = (document.activeElement as HTMLElement) ?? null;

    // Place active focus immediately on the modal action button with preventScroll: true
    const focusTimer = setTimeout(() => {
      if (initialFocus === 'cancel') {
        cancelBtnRef.current?.focus({ preventScroll: true });
      } else {
        confirmBtnRef.current?.focus({ preventScroll: true });
      }
    }, 20);

    const handler = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        cancelRef.current();
        return;
      }
      if (event.key === 'Enter') {
        const active = document.activeElement;
        if (active instanceof HTMLButtonElement && active === cancelBtnRef.current) {
          event.preventDefault();
          cancelRef.current();
          return;
        }
        if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) {
          return;
        }
        if (!busy) {
          event.preventDefault();
          confirmRef.current();
        }
        return;
      }
      if (event.key === 'Tab') {
        const focusable = panelRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey) {
          if (document.activeElement === first) {
            event.preventDefault();
            last?.focus({ preventScroll: true });
          }
        } else {
          if (document.activeElement === last) {
            event.preventDefault();
            first?.focus({ preventScroll: true });
          }
        }
      }
    };

    window.addEventListener('keydown', handler);

    return () => {
      clearTimeout(focusTimer);
      window.removeEventListener('keydown', handler);
      previouslyFocusedRef.current?.focus({ preventScroll: true });
    };
  }, [open, busy, initialFocus]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-xs overflow-y-auto"
      onClick={() => cancelRef.current()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={panelRef}
        onClick={(event) => event.stopPropagation()}
        className={cn(
          'animate-rise w-full overflow-hidden rounded-xl border bg-card shadow-pop outline-none my-auto',
          size === '2xl'
            ? 'max-w-2xl'
            : size === 'xl'
              ? 'max-w-xl'
              : size === 'md'
                ? 'max-w-md'
                : 'max-w-lg',
          tone === 'alarm' ? 'border-rust-600/40' : 'border-border',
        )}
      >
        <header className="flex items-center gap-2.5 border-b border-border bg-card px-5 py-4">
          <span
            aria-hidden="true"
            className={cn('h-2.5 w-2.5 rounded-full shrink-0', tone === 'alarm' ? 'bg-rust-600' : 'bg-clay-600')}
          />
          <h2 className="text-base font-semibold text-ink leading-tight">{title}</h2>
        </header>
        <div className="max-h-[75vh] overflow-y-auto px-5 py-4 text-sm text-ink leading-relaxed">
          {children}
        </div>
        <footer className="flex items-center justify-end gap-2 border-t border-border bg-cream/50 px-5 py-3.5">
          <Button
            ref={cancelBtnRef}
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => cancelRef.current()}
          >
            Annuler
          </Button>
          <Button
            ref={confirmBtnRef}
            type="button"
            variant={tone === 'alarm' ? 'danger' : 'primary'}
            size="sm"
            disabled={busy}
            onClick={onConfirm}
          >
            {busy ? 'En cours…' : confirmLabel}
          </Button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
